import DOMPurify from 'dompurify';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { ManageReleaseFormType } from '@/types';

import RequiredAsterik from '@/components/shared/required-asterik';
import { DatePicker } from '@/components/ui/date-picker';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import TipTapEditor from '@/components/ui/tip-tap-editor';
import useBreakpoint from '@/hooks/use-break-point';
import { cn } from '@/lib/utils';

const ManageReleaseFormFields = () => {
  const { t } = useTranslation();
  const { control } = useFormContext<ManageReleaseFormType>();

  const isTablet = useBreakpoint('tablet');

  return (
    <>
      <div className="pl-1 flex gap-4 flex-wrap">
        <Field
          className={cn('w-73 flex flex-col gap-2', {
            'w-full': isTablet,
          })}
        >
          <FieldLabel>
            {t('label.version_number')}
            <RequiredAsterik />
          </FieldLabel>
          <Controller
            name="version"
            control={control}
            rules={{
              required: true,
              minLength: 1,
            }}
            render={({ field }) => (
              <div className="flex flex-col gap-1 w-full">
                <Input
                  id="version"
                  placeholder={t('placeholder.enter_version_number')}
                  value={field.value}
                  onChange={(e) => {
                    const sanitizedValue = DOMPurify.sanitize(e.target.value);

                    field.onChange(sanitizedValue);
                  }}
                />
              </div>
            )}
          />
        </Field>

        <Field
          className={cn('w-73 flex flex-col gap-2', {
            'w-full': isTablet,
          })}
        >
          <FieldLabel>
            {t('label.release_date')}
            <RequiredAsterik />
          </FieldLabel>
          <Controller
            name="releaseDate"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <DatePicker
                placeholder={t('placeholder.enter_release_date')}
                value={field.value as unknown as Date}
                onChange={field.onChange}
                closeOnSelect
                isShowChevron={false}
              />
            )}
          />
        </Field>
      </div>

      <Field className="pl-1">
        <FieldLabel> {t('label.visibility')}</FieldLabel>
        <span className="flex gap-2 items-center text-sm">
          <Controller
            name="isPublished"
            control={control}
            render={({ field }) => (
              <Switch checked={field.value} onCheckedChange={field.onChange} />
            )}
          />
          {t('label.published_to_users')}
        </span>
      </Field>
      <Field className="pl-1 flex flex-col gap-2">
        <FieldLabel>
          {t('label.description')}
          <RequiredAsterik />
        </FieldLabel>
        <Controller
          control={control}
          name="description"
          rules={{
            required: true,

            validate: (value) => {
              if (!value) {
                return false;
              }

              const strippedContent = value.replace(/<[^>]*>?/gm, '').trim();

              return strippedContent.length > 0;
            },
          }}
          render={({ field }) => (
            <TipTapEditor
              className={cn('h-84.5 transition-colors', {
                'h-54.5': isTablet,
              })}
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </Field>
    </>
  );
};

export default ManageReleaseFormFields;
